import React, { useState, useMemo } from "react";
import { useQuery, useMutation, gql } from "@apollo/client";
import { UPLOAD_URL } from "../apolloClient";
import "../styles/Training.css";
import { 
  FiEdit2, FiTrash2, FiUpload, 
  FiChevronLeft, FiChevronRight, FiX,
  FiFileText, FiImage, FiCheck, FiGrid, FiSliders,
  FiSearch, FiDownload, FiFilter, FiEye,
  FiChevronsLeft, FiChevronsRight, FiDollarSign, FiPercent, FiClock
} from "react-icons/fi";

// GraphQL Definitions
const GET_TRAININGS = gql`
  query {
    getTrainings {
      id
      title
      amount
      gst
      days
      totalAmount
      imagePath
      shortDescription
      fullDescription
      createdAt
    }
  }
`;

const ADD_TRAINING = gql`
  mutation AddTraining(
    $title: String!
    $amount: Float!
    $gst: Float!
    $days: Int!
    $imagePath: String
    $shortDescription: String
    $fullDescription: String
  ) {
    addTraining(
      title: $title
      amount: $amount
      gst: $gst
      days: $days
      imagePath: $imagePath
      shortDescription: $shortDescription
      fullDescription: $fullDescription
    ) {
      id
      title
      amount
      gst
      days
      totalAmount
      imagePath
      shortDescription
      fullDescription
    }
  }
`;

const UPDATE_TRAINING = gql`
  mutation UpdateTraining(
    $id: ID!
    $title: String!
    $amount: Float!
    $gst: Float!
    $days: Int!
    $imagePath: String
    $shortDescription: String
    $fullDescription: String
  ) {
    updateTraining(
      id: $id
      title: $title
      amount: $amount
      gst: $gst
      days: $days
      imagePath: $imagePath
      shortDescription: $shortDescription
      fullDescription: $fullDescription
    ) {
      id
      title
      amount
      gst
      days
      totalAmount
      imagePath
      shortDescription
      fullDescription
    }
  }
`;

const DELETE_TRAINING = gql`
  mutation DeleteTraining($id: ID!) {
    deleteTraining(id: $id)
  }
`;

function TrainingPage() {
  // Form states
  const [title, setTitle] = useState("");
  const [amount, setAmount] = useState("");
  const [gst, setGst] = useState("");
  const [days, setDays] = useState("");
  const [imagePath, setImagePath] = useState("");
  const [shortDescription, setShortDescription] = useState("");
  const [fullDescription, setFullDescription] = useState("");
  
  // UI states
  const [editMode, setEditMode] = useState(null);
  const [viewMode, setViewMode] = useState("grid");
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [expandedCard, setExpandedCard] = useState(null);
  const [toast, setToast] = useState(null);
  
  // Filter, search and pagination states
  const [searchTerm, setSearchTerm] = useState("");
  const [daysFilter, setDaysFilter] = useState("all");
  const [amountFilter, setAmountFilter] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(8);

  const { loading, error: queryError, data, refetch } = useQuery(GET_TRAININGS, {
    fetchPolicy: "network-only",
  });

  const [addTraining] = useMutation(ADD_TRAINING);
  const [updateTraining] = useMutation(UPDATE_TRAINING);
  const [deleteTraining] = useMutation(DELETE_TRAINING);

  const trainings = data?.getTrainings || [];

  // Filter trainings based on search and filters
  const filteredTrainings = useMemo(() => {
    return trainings.filter(training => {
      // Text search
      const matchesSearch = !searchTerm || 
        training.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        training.shortDescription?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        training.fullDescription?.toLowerCase().includes(searchTerm.toLowerCase());

      // Days filter
      let matchesDays = true;
      if (daysFilter !== "all" && training.days) {
        const trainingDays = parseInt(training.days);
        switch(daysFilter) {
          case "short":
            matchesDays = trainingDays <= 3;
            break;
          case "medium":
            matchesDays = trainingDays > 3 && trainingDays <= 10;
            break;
          case "long":
            matchesDays = trainingDays > 10;
            break;
          default:
            matchesDays = true;
        }
      }

      // Amount filter
      let matchesAmount = true;
      if (amountFilter !== "all" && training.amount) {
        const trainingAmount = parseFloat(training.amount);
        switch(amountFilter) {
          case "low":
            matchesAmount = trainingAmount <= 5000;
            break;
          case "medium":
            matchesAmount = trainingAmount > 5000 && trainingAmount <= 20000;
            break;
          case "high":
            matchesAmount = trainingAmount > 20000;
            break;
          default:
            matchesAmount = true;
        }
      }

      return matchesSearch && matchesDays && matchesAmount;
    });
  }, [trainings, searchTerm, daysFilter, amountFilter]);

  // Pagination calculations
  const totalPages = Math.ceil(filteredTrainings.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentItems = filteredTrainings.slice(startIndex, endIndex);

  // Toast notification
  React.useEffect(() => {
    if (toast) {
      const timer = setTimeout(() => setToast(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [toast]);

  // Calculate total amount
  const calculateTotalAmount = (amt, gstPercent) => {
    const amountNum = parseFloat(amt) || 0;
    const gstNum = parseFloat(gstPercent) || 0;
    return amountNum + (amountNum * gstNum / 100);
  };

  // Export to CSV function
  const exportToCSV = () => {
    if (filteredTrainings.length === 0) {
      setToast("❌ No data to export!");
      return;
    }

    // Define CSV headers
    const headers = [
      "ID",
      "Title",
      "Amount",
      "GST (%)",
      "Total Amount",
      "Days",
      "Short Description",
      "Full Description",
      "Image URL",
      "Created At"
    ];

    // Prepare data rows
    const rows = filteredTrainings.map(t => [
      t.id,
      `"${(t.title || "").replace(/"/g, '""')}"`,
      t.amount || "",
      t.gst || "",
      t.totalAmount || calculateTotalAmount(t.amount, t.gst),
      t.days || "",
      `"${(t.shortDescription || "").replace(/"/g, '""')}"`,
      `"${(t.fullDescription || "").replace(/"/g, '""')}"`,
      t.imagePath || "",
      new Date(t.createdAt).toLocaleString()
    ]);

    // Combine headers and rows
    const csvContent = [
      headers.join(","),
      ...rows.map(row => row.join(","))
    ].join("\n");

    // Create blob and download
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    
    link.setAttribute("href", url);
    link.setAttribute("download", `trainings-${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = "hidden";
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setToast("✅ CSV exported successfully!");
  };

  // Image Upload Handler
  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      setToast("❌ Only images are allowed");
      return;
    }

    if (file.size > 20 * 1024 * 1024) {
      setToast("❌ File too large (max 20MB)");
      return;
    }

    const formData = new FormData();
    formData.append("file", file);
    formData.append("folder", "training");

    setIsUploading(true);
    setToast("⏳ Uploading image...");
    
    try {
      const res = await fetch(UPLOAD_URL, {
        method: "POST",
        body: formData,
      });

      if (!res.ok) throw new Error("Upload failed");
      const data = await res.json();

      if (data.success && data.url) {
        setImagePath(data.url);
        setToast("✅ Image uploaded successfully!");
      } else {
        throw new Error("Upload failed");
      }
    } catch (err) {
      console.error(err);
      setToast("❌ Upload error");
    } finally {
      setIsUploading(false);
    }
  };

  // Submit Handler
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!title.trim()) {
      setToast("❌ Title is required!");
      return;
    }

    const variables = {
      title,
      amount: parseFloat(amount) || 0,
      gst: parseFloat(gst) || 0,
      days: parseInt(days) || 0,
      imagePath,
      shortDescription,
      fullDescription,
    };

    try {
      if (editMode) {
        await updateTraining({ variables: { id: editMode, ...variables } });
        setToast("✅ Training updated successfully!");
      } else {
        await addTraining({ variables });
        setToast("✅ Training added successfully!");
      }
      resetForm();
      refetch();
      setCurrentPage(1);
    } catch (err) {
      setToast("❌ Error: " + err.message);
    }
  };

  const resetForm = () => {
    setTitle("");
    setAmount("");
    setGst("");
    setDays("");
    setImagePath("");
    setShortDescription("");
    setFullDescription("");
    setEditMode(null);
  };

  const handleEdit = (training) => {
    setEditMode(training.id);
    setTitle(training.title);
    setAmount(training.amount);
    setGst(training.gst);
    setDays(training.days);
    setImagePath(training.imagePath);
    setShortDescription(training.shortDescription || "");
    setFullDescription(training.fullDescription || "");
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this training?")) return;
    try {
      await deleteTraining({ variables: { id } });
      setToast("✅ Training deleted!");
      refetch();
      if (currentItems.length === 1 && currentPage > 1) {
        setCurrentPage(currentPage - 1);
      }
      if (expandedCard === id) {
        setExpandedCard(null);
      }
    } catch (err) {
      setToast("❌ Delete failed: " + err.message);
    }
  };

  // Toggle card expansion
  const toggleExpandCard = (id) => {
    setExpandedCard(expandedCard === id ? null : id);
  };

  // Slide Navigation
  const nextSlide = () => setCurrentSlide((prev) => (prev + 1) % filteredTrainings.length);
  const prevSlide = () => setCurrentSlide((prev) => (prev - 1 + filteredTrainings.length) % filteredTrainings.length);
  const goToSlide = (index) => setCurrentSlide(index);

  // Pagination handlers
  const goToPage = (page) => {
    setCurrentPage(page);
  };

  const nextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const prevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  if (loading) return <div className="loading-state"><div className="spinner"></div>Loading trainings...</div>;
  if (queryError) return <div className="error-state">Error: {queryError.message}</div>;

  return (
    <div className="training-container">
      {/* Toast Notification */}
      {toast && (
        <div className={`toast ${toast.includes('❌') ? 'error' : toast.includes('✅') ? 'success' : 'info'}`}>
          {toast}
        </div>
      )}

      {/* Header */}
      <div className="page-header">
        <div>
          <h1><FiFileText /> Training Management</h1>
          <p className="page-subtitle">
            Total: {trainings.length} trainings | 
            Showing: {filteredTrainings.length} filtered | 
            Page {currentPage} of {totalPages}
          </p>
        </div>
        {trainings.length > 0 && (
          <div className="header-actions">
            <div className="view-switch">
              <button 
                className={`view-btn ${viewMode === 'grid' ? 'active' : ''}`}
                onClick={() => setViewMode('grid')}
                title="Grid View"
              >
                <FiGrid /> Grid
              </button>
              <button 
                className={`view-btn ${viewMode === 'slides' ? 'active' : ''}`}
                onClick={() => setViewMode('slides')}
                title="Slides View"
                disabled={filteredTrainings.length === 0}
              >
                <FiSliders /> Slides
              </button>
            </div>
            <button 
              className="export-btn"
              onClick={exportToCSV}
              title="Export to CSV"
            >
              <FiDownload /> Export
            </button>
          </div>
        )}
      </div>

      {/* Search and Filter Bar */}
      <div className="filter-bar">
        <div className="search-box">
          <FiSearch />
          <input
            type="text"
            placeholder="Search by title or description..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setCurrentPage(1);
            }}
          />
          {searchTerm && (
            <button className="clear-search" onClick={() => setSearchTerm("")}>
              <FiX />
            </button>
          )}
        </div>
        
        <div className="training-filters">
          <div className="filter-group">
            <FiClock />
            <select 
              value={daysFilter} 
              onChange={(e) => {
                setDaysFilter(e.target.value);
                setCurrentPage(1);
              }}
              className="filter-select"
            >
              <option value="all">All Durations</option>
              <option value="short">Short (≤ 3 days)</option>
              <option value="medium">Medium (4-10 days)</option>
              <option value="long">Long (&gt; 10 days)</option>
            </select>
          </div>
          
          <div className="filter-group">
            <FiDollarSign />
            <select 
              value={amountFilter} 
              onChange={(e) => {
                setAmountFilter(e.target.value);
                setCurrentPage(1);
              }}
              className="filter-select"
            >
              <option value="all">All Prices</option>
              <option value="low">Low (≤ ₹5,000)</option>
              <option value="medium">Medium (₹5,001-₹20,000)</option>
             <option value="high">High (&gt; ₹20,000)</option>
            </select>
          </div>
        </div>
      </div>

      {/* Main Content - Side by Side Layout */}
      <div className="side-by-side-layout">
        {/* Left Side - Form */}
        <div className="left-side">
          <div className="form-container">
            <div className="form-header">
              <h3>{editMode ? 'Edit Training' : 'New Training'}</h3>
              {editMode && (
                <button className="cancel-btn" onClick={resetForm}>
                  <FiX /> Cancel
                </button>
              )}
            </div>
            
            <form onSubmit={handleSubmit} className="training-form">
              <div className="form-group">
                <label><FiFileText /> Title *</label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="Enter training title"
                  required
                />
              </div>
              
              <div className="form-row">
                <div className="form-group">
                  <label><FiDollarSign /> Amount (₹)</label>
                  <input
                    type="number"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    placeholder="0.00"
                    min="0"
                    step="0.01"
                    required
                  />
                </div>

                <div className="form-group">
                  <label><FiPercent /> GST (%)</label>
                  <input
                    type="number"
                    value={gst}
                    onChange={(e) => setGst(e.target.value)}
                    placeholder="0"
                    min="0"
                    max="100"
                    step="0.01"
                    required
                  />
                </div>

                <div className="form-group">
                  <label><FiClock /> Days</label>
                  <input
                    type="number"
                    value={days}
                    onChange={(e) => setDays(e.target.value)}
                    placeholder="0"
                    min="0"
                    required
                  />
                </div>
              </div>

              {amount && gst && (
                <div className="amount-summary">
                  <div className="amount-item">
                    <span>Base Amount:</span>
                    <span>₹{parseFloat(amount || 0).toFixed(2)}</span>
                  </div>
                  <div className="amount-item">
                    <span>GST ({gst}%):</span>
                    <span>₹{((parseFloat(amount || 0) * parseFloat(gst || 0)) / 100).toFixed(2)}</span>
                  </div>
                  <div className="amount-item total">
                    <span>Total Amount:</span>
                    <span>₹{calculateTotalAmount(amount, gst).toFixed(2)}</span>
                  </div>
                </div>
              )}

              <div className="form-group">
                <label><FiImage /> Image</label>
                <div className="upload-area">
                  <label className="upload-label">
                    <FiUpload />
                    {isUploading ? 'Uploading...' : 'Choose Image'}
                    <input 
                      type="file" 
                      accept="image/*" 
                      onChange={handleFileUpload}
                      hidden
                    />
                  </label>
                  {imagePath && (
                    <div className="upload-status">
                      <img src={imagePath} alt="Preview" />
                      <span><FiCheck /> Uploaded</span>
                    </div>
                  )}
                </div>
              </div>

              <div className="form-group">
                <label>Short Description</label>
                <textarea
                  rows="2"
                  value={shortDescription}
                  onChange={(e) => setShortDescription(e.target.value)}
                  placeholder="Brief description"
                  maxLength={120}
                />
              </div>

              <div className="form-group">
                <label>Full Description</label>
                <textarea
                  rows="3"
                  value={fullDescription}
                  onChange={(e) => setFullDescription(e.target.value)}
                  placeholder="Detailed description"
                />
              </div>

              <div className="form-actions">
                <button type="button" className="btn-outline" onClick={resetForm}>
                  Clear
                </button>
                <button type="submit" className="btn-primary">
                  {editMode ? 'Update' : 'Submit'}
                </button>
              </div>
            </form>
          </div>
        </div>

        {/* Right Side - Trainings */}
        <div className="right-side">
          <div className="trainings-container">
            <div className="trainings-header">
              <div className="header-left">
                <h3>All Trainings ({filteredTrainings.length})</h3>
                {(daysFilter !== "all" || amountFilter !== "all") && (
                  <div className="active-filter">
                    <FiFilter /> 
                    {daysFilter !== "all" && `Duration: ${daysFilter}`}
                    {daysFilter !== "all" && amountFilter !== "all" && " | "}
                    {amountFilter !== "all" && `Price: ${amountFilter}`}
                  </div>
                )}
              </div>
              <div className="header-right">
                <div className="pagination-info">
                  Showing {Math.min(startIndex + 1, filteredTrainings.length)} to {Math.min(endIndex, filteredTrainings.length)} of {filteredTrainings.length} entries
                </div>
                <div className="items-per-page">
                  <select 
                    value={itemsPerPage} 
                    onChange={(e) => {
                      setItemsPerPage(Number(e.target.value));
                      setCurrentPage(1);
                    }}
                  >
                    <option value="8">8 per page</option>
                    <option value="12">12 per page</option>
                    <option value="16">16 per page</option>
                    <option value="20">20 per page</option>
                  </select>
                </div>
              </div>
            </div>
            
            {viewMode === 'grid' ? (
              <>
                {filteredTrainings.length === 0 ? (
                  <div className="empty-content">
                    <FiFileText />
                    <p>No trainings found</p>
                    {searchTerm && <p className="empty-hint">Try changing your search term</p>}
                  </div>
                ) : (
                  <>
                    <div className="cards-grid">
                      {currentItems.map((training) => (
                        <div 
                          key={training.id} 
                          className={`card-item ${expandedCard === training.id ? 'expanded' : ''}`}
                        >
                          <div className="card-top">
                            {training.imagePath && (
                              <div className="card-img">
                                <img src={training.imagePath} alt={training.title} />
                              </div>
                            )}
                            <div className="card-actions">
                              <button onClick={() => handleEdit(training)} className="action-btn edit" title="Edit">
                                <FiEdit2 />
                              </button>
                              <button onClick={() => handleDelete(training.id)} className="action-btn delete" title="Delete">
                                <FiTrash2 />
                              </button>
                            </div>
                          </div>
                          
                          <div className="card-body">
                            <div className="training-meta">
                              <span className="days-badge">
                                <FiClock /> {training.days || 0} days
                              </span>
                              <span className="price-badge">
                                <FiDollarSign /> ₹{training.totalAmount ? parseFloat(training.totalAmount).toFixed(2) : calculateTotalAmount(training.amount, training.gst).toFixed(2)}
                              </span>
                            </div>
                            
                            <h4 className="card-title" title={training.title}>
                              {training.title}
                            </h4>
                            
                            {training.shortDescription && (
                              <p className="card-summary" title={training.shortDescription}>
                                {training.shortDescription}
                              </p>
                            )}
                            
                            {training.fullDescription && (
                              <div className={`card-description ${expandedCard === training.id ? 'expanded' : ''}`}>
                                {expandedCard === training.id ? training.fullDescription : `${training.fullDescription.substring(0, 40)}...`}
                                {!expandedCard && training.fullDescription.length > 40 && (
                                  <button 
                                    className="read-more"
                                    onClick={() => toggleExpandCard(training.id)}
                                  >
                                    Read more
                                  </button>
                                )}
                              </div>
                            )}
                            
                            <div className="price-details">
                              <div className="price-item">
                                <span>Base:</span>
                                <span>₹{parseFloat(training.amount || 0).toFixed(2)}</span>
                              </div>
                              <div className="price-item">
                                <span>GST:</span>
                                <span>{training.gst || 0}%</span>
                              </div>
                            </div>
                          </div>
                          
                          <div className="card-footer">
                            <button 
                              className="view-slide-btn"
                              onClick={() => {
                                setViewMode('slides');
                                const globalIndex = filteredTrainings.findIndex(t => t.id === training.id);
                                setCurrentSlide(globalIndex);
                              }}
                            >
                              <FiEye /> View Details
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Pagination Controls */}
                    {totalPages > 1 && (
                      <div className="pagination-controls">
                        <div className="pagination-buttons">
                          <button 
                            className="pagination-btn first"
                            onClick={() => goToPage(1)}
                            disabled={currentPage === 1}
                          >
                            <FiChevronsLeft />
                          </button>
                          <button 
                            className="pagination-btn prev"
                            onClick={prevPage}
                            disabled={currentPage === 1}
                          >
                            <FiChevronLeft />
                          </button>
                          
                          {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                            let pageNum;
                            if (totalPages <= 5) {
                              pageNum = i + 1;
                            } else if (currentPage <= 3) {
                              pageNum = i + 1;
                            } else if (currentPage >= totalPages - 2) {
                              pageNum = totalPages - 4 + i;
                            } else {
                              pageNum = currentPage - 2 + i;
                            }
                            
                            return pageNum <= totalPages ? (
                              <button
                                key={pageNum}
                                className={`pagination-btn ${currentPage === pageNum ? 'active' : ''}`}
                                onClick={() => goToPage(pageNum)}
                              >
                                {pageNum}
                              </button>
                            ) : null;
                          })}
                          
                          <button 
                            className="pagination-btn next"
                            onClick={nextPage}
                            disabled={currentPage === totalPages}
                          >
                            <FiChevronRight />
                          </button>
                          <button 
                            className="pagination-btn last"
                            onClick={() => goToPage(totalPages)}
                            disabled={currentPage === totalPages}
                          >
                            <FiChevronsRight />
                          </button>
                        </div>
                        <div className="page-info">
                          Page {currentPage} of {totalPages}
                        </div>
                      </div>
                    )}
                  </>
                )}
              </>
            ) : (
              <div className="slides-view">
                <div className="slides-header">
                  <button className="back-btn" onClick={() => setViewMode('grid')}>
                    <FiChevronLeft /> Back to Grid
                  </button>
                  <div className="slide-nav-info">
                    <span className="slide-counter">
                      {currentSlide + 1} / {filteredTrainings.length}
                    </span>
                  </div>
                </div>

                <div className="slide-container">
                  <button className="nav-btn prev" onClick={prevSlide} disabled={filteredTrainings.length <= 1}>
                    <FiChevronLeft />
                  </button>

                  <div className="slide-content">
                    {filteredTrainings[currentSlide] && (
                      <div className="slide-item">
                        <div className="slide-media">
                          {filteredTrainings[currentSlide].imagePath ? (
                            <img src={filteredTrainings[currentSlide].imagePath} alt={filteredTrainings[currentSlide].title} />
                          ) : (
                            <div className="no-media">
                              <FiImage />
                              <p>No Image</p>
                            </div>
                          )}
                        </div>
                        
                        <div className="slide-info">
                          <h2>{filteredTrainings[currentSlide].title}</h2>
                          
                          <div className="slide-meta">
                            <span className="slide-days">
                              <FiClock /> {filteredTrainings[currentSlide].days || 0} days
                            </span>
                            <span className="slide-price">
                              <FiDollarSign /> Total: ₹{
                                filteredTrainings[currentSlide].totalAmount 
                                  ? parseFloat(filteredTrainings[currentSlide].totalAmount).toFixed(2)
                                  : calculateTotalAmount(
                                      filteredTrainings[currentSlide].amount,
                                      filteredTrainings[currentSlide].gst
                                    ).toFixed(2)
                              }
                            </span>
                          </div>
                          
                          <div className="slide-pricing">
                            <div className="pricing-item">
                              <span>Base Amount:</span>
                              <span>₹{parseFloat(filteredTrainings[currentSlide].amount || 0).toFixed(2)}</span>
                            </div>
                            <div className="pricing-item">
                              <span>GST ({filteredTrainings[currentSlide].gst || 0}%):</span>
                              <span>₹{((parseFloat(filteredTrainings[currentSlide].amount || 0) * parseFloat(filteredTrainings[currentSlide].gst || 0)) / 100).toFixed(2)}</span>
                            </div>
                          </div>
                          
                          {filteredTrainings[currentSlide].shortDescription && (
                            <div className="slide-excerpt">
                              <h4>Summary</h4>
                              <p>{filteredTrainings[currentSlide].shortDescription}</p>
                            </div>
                          )}
                          
                          {filteredTrainings[currentSlide].fullDescription && (
                            <div className="slide-details">
                              <h4>Description</h4>
                              <p className="slide-full-desc">{filteredTrainings[currentSlide].fullDescription}</p>
                            </div>
                          )}
                          
                          <div className="slide-controls">
                            <button 
                              className="btn-slide edit"
                              onClick={() => {
                                handleEdit(filteredTrainings[currentSlide]);
                                setViewMode('grid');
                              }}
                            >
                              <FiEdit2 /> Edit
                            </button>
                            <button 
                              className="btn-slide delete"
                              onClick={() => {
                                if (window.confirm('Delete this training?')) {
                                  handleDelete(filteredTrainings[currentSlide].id);
                                  if (filteredTrainings.length === 1) {
                                    setViewMode('grid');
                                  } else {
                                    setCurrentSlide(Math.max(0, currentSlide - 1));
                                  }
                                }
                              }}
                            >
                              <FiTrash2 /> Delete
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  <button className="nav-btn next" onClick={nextSlide} disabled={filteredTrainings.length <= 1}>
                    <FiChevronRight />
                  </button>
                </div>

                {filteredTrainings.length > 1 && (
                  <div className="slide-dots">
                    {filteredTrainings.map((_, index) => (
                      <button
                        key={index}
                        className={`dot ${index === currentSlide ? 'active' : ''}`}
                        onClick={() => goToSlide(index)}
                        title={`Go to slide ${index + 1}`}
                      />
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default TrainingPage;